import { LitElement, html, css, unsafeCSS } from 'lit';
import { api } from '../api.js';
import { auth } from '../auth.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Search, Trash2 } from 'lucide';
import './mv-table.js';
import { lucideIcon } from '../icon.js';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import { formatDate as formatDateNL, formatTime as formatTimeNL } from '../utils/datetime.js';
import { displayName } from '@mv/models';
import styles from '../styles/admin-users.css?inline';

const roleLabel = (r) =>
  r === 'owner' ? 'Eigenaar' : r === 'manager' ? 'Beheerder' : r === 'member' ? 'Lid' : (r || '—');

/**
 * System-wide user overview (Sysadmin only). Lists all users with their
 * organisations, last login and Sysadmin flag. Clicking a user opens a detail
 * popup (organisations + roles, last login, Sysadmin toggle).
 * Data: GET /api/admin/users ; toggle: PUT /api/admin/users/:id/sysadmin.
 */
export class AdminUsers extends LitElement {
  static styles = css`${unsafeCSS(styles)}`;

  static properties = {
    users: { type: Array },
    loading: { type: Boolean },
    error: { type: String },
    searchQuery: { type: String },
    selectedUser: { type: Object },
    busy: { type: Boolean },
    _editFirst: { type: String, state: true },
    _editLast: { type: String, state: true },
  };

  constructor() {
    super();
    this.users = [];
    this.loading = false;
    this.error = null;
    this.searchQuery = '';
    this.selectedUser = null;
    this.busy = false;
    this._editFirst = '';
    this._editLast = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadUsers();
    this._onEsc = (e) => { if (e.key === 'Escape' && this.selectedUser) this.closeUser(); };
    document.addEventListener('keydown', this._onEsc);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onEsc);
  }

  async loadUsers() {
    this.loading = true;
    this.error = null;
    try {
      this.users = await api.getAdminUsers();
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
    }
  }

  handleSearchInput(e) { this.searchQuery = e.target.value; }

  get filteredUsers() {
    if (!this.searchQuery) return this.users;
    const q = this.searchQuery.toLowerCase();
    return this.users.filter(u =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.companies || []).some(c => (c.company_name || '').toLowerCase().includes(q))
    );
  }

  formatDate(d) {
    return d ? formatDateNL(d) : '—';
  }

  formatLastLogin(d) {
    if (!d) return 'Nog nooit ingelogd';
    return `${formatDateNL(d)} om ${formatTimeNL(d)}`;
  }

  openUser(u) { this.selectedUser = u; this._editFirst = u.first_name || ''; this._editLast = u.last_name || ''; }
  closeUser() { this.selectedUser = null; }

  get _nameDirty() {
    const u = this.selectedUser; if (!u) return false;
    return (this._editFirst || '') !== (u.first_name || '') || (this._editLast || '') !== (u.last_name || '');
  }

  async _saveName() {
    const u = this.selectedUser; if (!u || !this._nameDirty) return;
    this.busy = true;
    try {
      const res = await api.updateAdminUser(u.id, { firstName: this._editFirst.trim(), lastName: this._editLast.trim() });
      // update the row in place + the open user
      this.users = this.users.map(x => x.id === u.id ? { ...x, first_name: res.first_name, last_name: res.last_name } : x);
      this.selectedUser = { ...u, first_name: res.first_name, last_name: res.last_name };
    } catch (e) {
      alertDialog({ message: `Naam opslaan mislukt: ${e.message}` });
    } finally {
      this.busy = false;
    }
  }

  async toggleSysadmin(u) {
    const next = !u.is_system_admin;
    // Guard: never let a sysadmin strip their own rights (also enforced server-side).
    if (!next && u.id === auth.getUser()?.id) return;
    // Double-check before granting: a Sysadmin can make far-reaching backend changes.
    if (next) {
      const ok = await confirmDialog({
        title: 'Weet je het zeker?',
        message: `Je staat op het punt ${u.email} systeembeheerder te maken. Een systeembeheerder heeft toegang tot het volledige Beheer-gedeelte en kan ingrijpende wijzigingen maken aan de achterkant van de applicatie — alle bedrijven, gebruikers en rechten. Geef dit alleen aan mensen die je volledig vertrouwt.`,
        confirmLabel: 'Ja, sysadmin maken',
        danger: true,
      });
      if (!ok) return;
    }
    this.busy = true;
    try {
      await api.setUserSysadmin(u.id, next);
      u.is_system_admin = next;
      this.users = [...this.users];
      if (this.selectedUser?.id === u.id) this.selectedUser = { ...u };
    } catch (e) {
      alertDialog({ message: `Mislukt: ${e.message}` });
    } finally {
      this.busy = false;
    }
  }

  // Refresh the selected user (and the row in the table) from the backend.
  async _reloadSelected() {
    const id = this.selectedUser?.id;
    await this.loadUsers();
    if (id) {
      const fresh = this.users.find((x) => x.id === id);
      this.selectedUser = fresh || null;
    }
  }

  async changeMembershipRole(u, company, role) {
    if (role === company.role) return;
    const ok = await confirmDialog({
      title: 'Rol wijzigen?',
      message: `Je wijzigt de rol van ${u.email} bij ${company.company_name} naar ${roleLabel(role)}. Dit bepaalt wat deze gebruiker binnen dat bedrijf mag.`,
      confirmLabel: 'Ja, rol wijzigen',
    });
    if (!ok) { this.users = [...this.users]; return; } // re-render to reset the select
    this.busy = true;
    try {
      await api.setUserMembershipRole(u.id, company.company_id, role);
      await this._reloadSelected();
    } catch (e) {
      alertDialog({ message: `Rol wijzigen mislukt: ${e.message}` });
      this.users = [...this.users];
    } finally {
      this.busy = false;
    }
  }

  async removeMembership(u, company) {
    const ok = await confirmDialog({
      title: 'Toegang intrekken?',
      message: `${u.email} verliest direct toegang tot ${company.company_name} en alle bijbehorende projecten. Dit kan niet ongedaan worden gemaakt — je kunt ze later opnieuw uitnodigen.`,
      confirmLabel: 'Ja, toegang intrekken',
      danger: true,
    });
    if (!ok) return;
    this.busy = true;
    try {
      await api.removeUserMembership(u.id, company.company_id);
      await this._reloadSelected();
    } catch (e) {
      alertDialog({ message: `Intrekken mislukt: ${e.message}` });
    } finally {
      this.busy = false;
    }
  }

  get columns() {
    return [
      { id: 'name', title: 'Naam', render: (row) => (row.first_name || row.last_name)
        ? html`${[row.first_name, row.last_name].filter(Boolean).join(' ')}`
        : html`<span style="color:var(--mv-text-sub,#6c757d);">—</span>` },
      { id: 'email', title: 'E-mailadres' },
      { id: 'companies', title: 'Organisaties', render: (row) => html`${(row.companies || []).length}` },
      { id: 'last_login_at', title: 'Laatste login', sortable: true,
        sortValue: (row) => row.last_login_at || '', defaultSortDir: 'desc',
        render: (row) => this.formatDate(row.last_login_at) },
      {
        id: 'is_system_admin', title: 'Sysadmin',
        render: (row) => row.is_system_admin
          ? html`<span class="role-badge admin">Sysadmin</span>`
          : html`<span style="color:var(--mv-text-sub,#6c757d);">—</span>`,
      },
    ];
  }

  render() {
    const u = this.selectedUser;
    return html`
      <div class="dashboard-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem;">
        <h1 style="margin:0; font-size:1.5rem; font-weight:700; color:var(--mv-text);">Gebruikers</h1>
      </div>

      ${this.error ? html`<div class="error-banner">${this.error}</div>` : ''}

      <div class="search-container">
        <div class="search-input-wrapper">
          <span class="search-icon">${unsafeSVG(lucideIcon(Search, 18))}</span>
          <input type="text" class="search-input"
            placeholder="Zoeken op e-mailadres of organisatie..."
            .value=${this.searchQuery} @input=${this.handleSearchInput} />
        </div>
      </div>

      <mv-table
        .columns=${this.columns}
        .data=${this.filteredUsers}
        emptyText=${this.loading ? 'Gebruikers laden…' : 'Geen gebruikers gevonden.'}
        @row-click=${(e) => this.openUser(e.detail.row)}
      ></mv-table>

      ${u ? html`
        <div @click=${(e) => e.target === e.currentTarget && this.closeUser()}
          style="position:fixed; inset:0; background:rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px;">
          <div style="background:var(--mv-surface,#fff); border-radius:16px; width:100%; max-width:480px; padding:24px; box-shadow:0 12px 40px rgba(0,0,0,0.2);">
            <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:20px;">
              <div>
                <h2 style="margin:0; font-size:1.1rem; color:var(--mv-text);">${u.email}</h2>
                <div style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d); margin-top:4px;">
                  Laatste login: ${this.formatLastLogin(u.last_login_at)}
                </div>
              </div>
              <button @click=${this.closeUser} style="background:none; border:none; font-size:1.4rem; line-height:1; color:var(--mv-text-sub,#6c757d); cursor:pointer;">&times;</button>
            </div>

            <div style="font-size:0.72rem; font-weight:700; letter-spacing:1px; color:var(--mv-text-sub,#6c757d); margin-bottom:8px;">NAAM</div>
            <div style="display:flex; gap:8px; align-items:center; margin-bottom:20px; flex-wrap:wrap;">
              <input type="text" placeholder="Voornaam" .value=${this._editFirst} ?disabled=${this.busy}
                @input=${(e) => this._editFirst = e.target.value}
                style="flex:1; min-width:120px; padding:9px 11px; border:1px solid var(--mv-border); border-radius:8px; font-size:0.9rem; color:var(--mv-text); background:var(--mv-bg); box-sizing:border-box;">
              <input type="text" placeholder="Achternaam" .value=${this._editLast} ?disabled=${this.busy}
                @input=${(e) => this._editLast = e.target.value}
                style="flex:1; min-width:120px; padding:9px 11px; border:1px solid var(--mv-border); border-radius:8px; font-size:0.9rem; color:var(--mv-text); background:var(--mv-bg); box-sizing:border-box;">
              <button @click=${this._saveName} ?disabled=${this.busy || !this._nameDirty}
                style="padding:9px 14px; border-radius:8px; border:1px solid transparent; background:var(--mv-primary,#4b9d35); color:#fff; font-weight:600; cursor:pointer; ${this.busy || !this._nameDirty ? 'opacity:.5; cursor:not-allowed;' : ''}">Opslaan</button>
            </div>

            <div style="font-size:0.72rem; font-weight:700; letter-spacing:1px; color:var(--mv-text-sub,#6c757d); margin-bottom:8px;">ORGANISATIES</div>
            ${(u.companies || []).length === 0 ? html`
              <div style="color:var(--mv-text-sub,#6c757d); font-size:0.9rem; margin-bottom:20px;">Geen organisaties.</div>
            ` : html`
              <div style="display:flex; flex-direction:column; gap:6px; margin-bottom:20px;">
                ${(u.companies || []).map(c => html`
                  <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; padding:10px 12px; border:1px solid var(--mv-border); border-radius:8px;">
                    <span style="font-weight:600; color:var(--mv-text); flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${c.company_name}</span>
                    ${c.role === 'owner' ? html`
                      <span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);" title="Eigenaarschap wijzig je via 'Eigenaarschap overdragen' op de Team-pagina">${roleLabel(c.role)}</span>
                    ` : html`
                      <select .value=${c.role} ?disabled=${this.busy}
                        @change=${(e) => this.changeMembershipRole(u, c, e.target.value)}
                        style="padding:5px 8px; border-radius:6px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; font-size:0.82rem; cursor:pointer;">
                        <option value="manager" ?selected=${c.role === 'manager'}>Beheerder</option>
                        <option value="member" ?selected=${c.role === 'member'}>Lid</option>
                      </select>
                      <button @click=${() => this.removeMembership(u, c)} ?disabled=${this.busy} title="Toegang intrekken"
                        style="display:flex; align-items:center; justify-content:center; width:30px; height:30px; border-radius:6px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-danger,#dc3545); cursor:pointer; flex-shrink:0;">
                        ${unsafeSVG(lucideIcon(Trash2, 15))}
                      </button>
                    `}
                  </div>
                `)}
              </div>
            `}

            <div style="display:flex; justify-content:space-between; align-items:center; padding-top:16px; border-top:1px solid var(--mv-border);">
              <div>
                <div style="font-weight:600; color:var(--mv-text);">Systeembeheerder</div>
                <div style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">Toegang tot het hele Beheer-gedeelte.</div>
              </div>
              ${u.id === auth.getUser()?.id ? html`
                <span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d); max-width:160px; text-align:right;">Je kunt je eigen rechten niet wijzigen</span>
              ` : html`
                <button @click=${() => this.toggleSysadmin(u)} ?disabled=${this.busy}
                  style="padding:8px 14px; border-radius:8px; border:1px solid ${u.is_system_admin ? 'var(--mv-danger,#dc3545)' : 'var(--mv-primary)'}; background:none; color:${u.is_system_admin ? 'var(--mv-danger,#dc3545)' : 'var(--mv-primary)'}; font-weight:600; cursor:pointer;">
                  ${this.busy ? '…' : (u.is_system_admin ? 'Intrekken' : 'Sysadmin maken')}
                </button>
              `}
            </div>

            <div style="display:flex; justify-content:space-between; align-items:center; padding-top:16px; border-top:1px solid var(--mv-border);">
              <div>
                <div style="font-weight:600; color:var(--mv-text);">Tweestapsverificatie</div>
                <div style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">Reset 2FA als de gebruiker zijn authenticator kwijt is.</div>
              </div>
              <button @click=${() => this.reset2fa(u)} ?disabled=${this.busy}
                style="padding:8px 14px; border-radius:8px; border:1px solid var(--mv-border); background:none; color:var(--mv-text); font-weight:600; cursor:pointer;">
                ${this.busy ? '…' : '2FA resetten'}
              </button>
            </div>
          </div>
        </div>
      ` : ''}
    `;
  }

  async reset2fa(u) {
    const ok = await confirmDialog({
      title: 'Tweestapsverificatie resetten?',
      message: `2FA wordt uitgeschakeld voor ${u.email}. Bij de volgende login kunnen ze zonder code inloggen${u.is_system_admin ? ' en worden ze meteen gevraagd 2FA opnieuw in te stellen' : ''}.`,
      confirmLabel: '2FA resetten', danger: true,
    });
    if (!ok) return;
    this.busy = true;
    try {
      await api.resetUser2fa(u.id);
      await alertDialog({ title: 'Gereset', message: 'Tweestapsverificatie is uitgeschakeld voor deze gebruiker.' });
    } catch (e) {
      await alertDialog({ title: 'Mislukt', message: e.message });
    } finally {
      this.busy = false;
    }
  }
}

customElements.define('admin-users', AdminUsers);
